import React, { JSX, useEffect, useMemo, useState } from "react";
import { exportCustomerTierExcel, exportTierAuditLogExcel, getCustomerTierOverview } from "../../datasource/CustomerTierProgramDatasource";
import type { AxiosResponse } from "axios";
import dayjs from "dayjs";
import { DownloadOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";
import { CardContainer } from "../../components/Card/CardContainer";
import PageTitle from "../../components/PageTitle/PageTitle";
import AntdTabs from "../../components/AntdTabs/AntdTabs";
import Text from "../../components/Text/Text";
import Button from "../../components/Button/Button";
import { useLocalStorage } from "../../hook/useLocalStorage";
import { SHOP_TIER_PAGE_DEFAULT, useShopTierPageState } from "../../store/shopTierPageState";
import { resolveShopTierConfig } from "./config";
import { generateShopTierMockData } from "./mockData";
import ShopListTab from "./ShopListTab";
import { tierAuditLogActivityOptions } from "./tierAuditLogActivity";
import { normalizePageSize } from "./TierTablePagination";
import AuditLogTab from "./AuditLogTab";
import ExportShopTierModal from "./ExportShopTierModal";
import ConfigTab from "./ConfigTab";
import SearchInput from "../../components/Input/SearchInput";
import Select from "../../components/Select/Select";
import {
  KpiRow,
  KpiChip,
  KpiSkeleton,
  KpiSkeletonLine,
  KpiValue,
  KpiUnit,
  KpiTitle,
  KpiSub,
  kpiTextColor,
  ToolbarRow,
  NavWarningBanner,
  NavWarningLink,
} from "./styled";
import { useLocation, useNavigate } from "react-router";
import { useQuery } from "react-query";
import { zoneDatasource } from "../../datasource/ZoneDatasource";
import color from "../../resource/color";
import { useMessageContext } from "../../context/MessageProvider";
import { useNotificationContext } from "../../context/NotificationProvider";
import { useRecoilValue } from "recoil";
import { checkPermission } from "../../components/Permission/Permission";
import { roleAtom } from "../../store/RoleAtom";
import ProgressNotification from "../../components/Spin/ProgressNotification";
import { getTierAvailableYear } from "../../datasource/TierProgramDatasource";

const EXPORT_NOTIFICATION_KEY = "shopTierExport";
// How long the finished (100%) ring stays up before the file is handed to the browser.
const EXPORT_DONE_HOLD_MS = 600;

interface TierLevel {
  tierLevelId: string;
  tierName: string;
  tierRank: number;
  color: string;
  minTarget: number;
  maxTarget: number | null;
  customerCount: number;
}

function ShopTierPage(): JSX.Element {
  const [company] = useLocalStorage("profile", null);
  const config = resolveShopTierConfig(company?.company);
  const mockData = useMemo(() => generateShopTierMockData(config), [config]);
  const location = useLocation();
  const navigate = useNavigate();
  const roleData = useRecoilValue(roleAtom);
  const canCreate = checkPermission(["shopTier", "create"], roleData);
  const canExport = checkPermission(["shopTier", "excel"], roleData);

  // Tab, filters, sort and pagination live in Recoil (store/shopTierPageState) so they are
  // still there after visiting another page and coming back.
  const [pageState, patchPageState] = useShopTierPageState();
  const {
    selectedTab,
    search,
    isNavPending,
    zone,
    year,
    action,
    status,
    tierFilter,
    sortField,
    sortDirection,
  } = pageState;

  // A different company (another login in the same tab) must not inherit the previous filters.
  useEffect(() => {
    if (company?.company && pageState.company !== company.company) {
      patchPageState({ ...SHOP_TIER_PAGE_DEFAULT, company: company.company });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.company]);

  // Pages that send the user back with a tab (e.g. create benefit → "config") still win.
  useEffect(() => {
    const tab = (location.state as { tab?: string } | null)?.tab;
    if (tab) patchPageState({ selectedTab: tab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Changing any filter starts both tables from page 1 again (done here, on the change itself,
  // so returning to the page with a stored page number does not reset it).
  const setFilter = (filter: Partial<typeof pageState>) =>
    patchPageState({ ...filter, shopListPage: 1, auditPage: 1 });
  const setSelectedTab = (selectedTab: string) => patchPageState({ selectedTab });
  const setSearch = (search: string) => setFilter({ search });
  const setIsNavPending = (isNavPending: boolean) => setFilter({ isNavPending });
  const setZone = (zone: string | null) => setFilter({ zone });
  const setYear = (year: string | null) => setFilter({ year });
  const setAction = (action: string | null) => setFilter({ action });
  const setStatus = (status: string) => setFilter({ status });
  const toggleTierFilter = (tierLevelId: string) =>
    setFilter({ tierFilter: tierFilter === tierLevelId ? null : tierLevelId });

  // ASC → DESC → off. "Off" clears the field too so the API falls back to the default order.
  const handleSortChange = (field: string) => {
    if (sortField !== field) {
      patchPageState({ sortField: field, sortDirection: "ASC" });
    } else if (sortDirection === "ASC") {
      patchPageState({ sortDirection: "DESC" });
    } else {
      patchPageState({ sortField: undefined, sortDirection: undefined });
    }
  };
  const { messageApi } = useMessageContext();
  const { api: notificationApi } = useNotificationContext();
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { data : customerTierOverview, isLoading: isOverviewLoading } = useQuery(
    ["tier-available-year", company?.company, tierFilter, zone, search, status, isNavPending],
    () => getCustomerTierOverview(isNavPending ? { 
      company: company?.company,
      tierLevelId: tierFilter ?? undefined,
      customerZone: zone ?? undefined,
      search: search || undefined,
      status: status !== "ALL" ? status : "ALL",
      isUpdateNav: false
    } : { 
      company: company?.company,
      tierLevelId: tierFilter ?? undefined,
      customerZone: zone ?? undefined,
      search: search || undefined,
      status: status !== "ALL" ? status : "ALL",
    }),
    // keepPreviousData: after the first load, a filter change keeps showing the last overview
    // (same coloured chips) until the new numbers arrive, instead of emptying the row.
    { enabled: !!company, refetchOnWindowFocus: false, keepPreviousData: true },
  );
  const currentYear = customerTierOverview?.responseData?.year;
  const pendingNavCount = customerTierOverview?.responseData?.pendingNavCount || 0;
  const overviewUpdatedAt: string = customerTierOverview?.responseData?.updatedAt
    ? dayjs(customerTierOverview.responseData.updatedAt).format("DD/MM/BBBB, HH:mm")
    : "-";
  const tierLevels: TierLevel[] = customerTierOverview?.responseData?.tierLevels || [];

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear() + 543;
    return [...[currentYear,currentYear-1,currentYear-2,currentYear-3,currentYear-4].map((y) => ({ key: y, value: y, label: `ปี ${y}` }))];
  }, []);

  const actionOptions = useMemo(() => tierAuditLogActivityOptions(company?.company), [company?.company]);

  const { data: zones } = useQuery(
    ["zones-by-company", company?.company],
    async() => {
      const data = await zoneDatasource.getAllZoneByCompany(company?.company);
      return data.map((res)=>({ key: res.zoneId, value: res.zoneName, label: res.zoneName }));
    },
    { enabled: !!company?.company, refetchOnWindowFocus: false }
  );

  const statusOptions = useMemo(() => {
    return [{ key: "ALL", value: "ALL", label: "สถานะทั้งหมด" }, { key: "PENDING_REVIEW", value: "PENDING_REVIEW", label: "รอพิจารณาปรับระดับ" }];
  }, []);
  const saveExcelResponse = (res: AxiosResponse<ArrayBuffer> | undefined, baseName: string): boolean => {
    const contentType = String(res?.headers?.["content-type"] ?? "");
    if (!res?.data || !contentType.includes("spreadsheetml")) return false;
    const date = new Date();
    const stamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(
      date.getDate(),
    ).padStart(2, "0")}`;
    const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `${baseName}-${stamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
    return true;
  };

  // Same bottom-right "กำลังเตรียมไฟล์ของคุณ รอสักครู่!" card as the other exports in the app
  // (ProgressNotification): spinning dots while the request runs, a full ring when the file is
  // ready. Unlike startUploadProcess it tracks the real request instead of a fixed fake timer.
  const showExportProgress = (progress: number) => {
    notificationApi.open({
      message: <ProgressNotification progress={progress} label='กำลังเตรียมไฟล์ของคุณ รอสักครู่!' />,
      placement: "bottomRight",
      duration: null,
      key: EXPORT_NOTIFICATION_KEY,
    });
  };

  const { data : availableYears } = useQuery(
    ["tier-available-year", company],
    async() => {
      const res = await getTierAvailableYear({ company : company?.company, isHistory: true });
      return res;
    },
    { enabled: !!company, refetchOnWindowFocus: false },
  );

  const runExport = async (
    request: () => Promise<AxiosResponse<ArrayBuffer> | undefined>,
    baseName: string,
    errorText: string,
  ) => {
    if (isExporting) return;
    setIsExporting(true);
    showExportProgress(0);
    try {
      const res = await request();
      const contentType = String(res?.headers?.["content-type"] ?? "");
      if (!res?.data || !contentType.includes("spreadsheetml")) {
        notificationApi.destroy(EXPORT_NOTIFICATION_KEY);
        messageApi.error(errorText);
        return;
      }
      showExportProgress(100);
      await new Promise((resolve) => setTimeout(resolve, EXPORT_DONE_HOLD_MS));
      notificationApi.destroy(EXPORT_NOTIFICATION_KEY);
      saveExcelResponse(res, baseName);
    } catch (err) {
      console.log(err);
      notificationApi.destroy(EXPORT_NOTIFICATION_KEY);
      messageApi.error(errorText);
    } finally {
      setIsExporting(false);
    }
  };

  // Same filters and sort as the ShopListTab table; the file contains every matching row.
  // File name: shops-tier-<year as shown in the modal>-<company>-<yyyyMMdd>.
  const handleExportShopTier = (exportYear: string) => {
    const selectedYear = availableYears?.responseData?.historyPrograms?.find(
      (y: { tierProgramId: string }) => y.tierProgramId === exportYear,
    )?.year;
    return runExport(
      () =>
        exportCustomerTierExcel({
          company: company?.company ?? "",
          tierLevelId: tierFilter ?? undefined,
          customerZone: zone ?? undefined,
          search: search || undefined,
          status: status !== "ALL" ? status : "ALL",
          sortBy: sortField || "updatedAt",
          sortOrder: sortDirection || "DESC",
          isUpdatedNav: isNavPending,
          tierProgramId: exportYear,
        }),
      `shops-tier-${selectedYear ?? ""}-${company?.company ?? ""}`,
      "Export ข้อมูลระดับร้านค้าไม่สำเร็จ",
    );
  };

  // Same filters as the AuditLogTab table (mirrors its getTierAuditLog params).
  // File name follows the backend: audit-log-tier-<yyyyMMdd>, no company.
  const handleExportAuditLog = () =>
    runExport(
      () =>
        exportTierAuditLogExcel({
          company: company?.company ?? "",
          search: search || undefined,
          customerZone: zone || undefined,
          year: year && year !== "ALL" ? Number(year) : undefined,
          activity: action ? [action] : undefined,
        }),
      "audit-log-tier",
      "Export Audit Log ไม่สำเร็จ",
    );

  const tabsData = [
    {
      key: "history",
      label: "ร้านค้าทั้งหมด",
      children: (
        <ShopListTab
          config={config}
          search={search}
          zone={zone}
          tierFilter={tierFilter}
          status={status}
          isNavPending={isNavPending}
          sortField={sortField}
          sortDirection={sortDirection}
          currentYear={currentYear}
          onSortChange={handleSortChange}
          page={pageState.shopListPage}
          take={normalizePageSize(pageState.shopListTake)}
          onPageChange={(shopListPage) => patchPageState({ shopListPage })}
          onTakeChange={(shopListTake) => patchPageState({ shopListTake, shopListPage: 1 })}
        />
      ),
    },
    {
      key: "audit",
      label: "Audit Log",
      children: (
        <AuditLogTab
          search={search}
          zone={zone}
          year={year!}
          action={action}
          page={pageState.auditPage}
          take={normalizePageSize(pageState.auditTake)}
          onPageChange={(auditPage) => patchPageState({ auditPage })}
          onTakeChange={(auditTake) => patchPageState({ auditTake, auditPage: 1 })}
        />
      ),
    },
    {
      key: "config",
      label: "ตั้งค่าระดับ",
      children: <ConfigTab config={config} configYears={mockData.configYears} />,
    },
  ];
  
  const rangeTier = (minTarget: number | null, maxTarget: number | null) => {
    if(company.company === "ICPL"){
      if (minTarget !== null && maxTarget !== null && minTarget !== 0) {
        return `${(minTarget / 1000000).toFixed(0)}-${Number((maxTarget / 1000000).toFixed(0))-1}M฿`;
      } else if (minTarget !== null && minTarget !== 0) {
        return `${(minTarget / 1000000).toFixed(0)}M฿ ขึ้นไป`;
      } else if (minTarget === 0 && maxTarget !== null) {
        return `ต่ำกว่า ${(maxTarget / 1000000).toFixed(0)}M฿`;
      } else {
        return "-";
      }
    }
    // Tonnes with thousands separators: 1000 → "1,000".
    const tons = (value: number) => Number(value).toLocaleString("th-TH");
    if (minTarget !== null && maxTarget !== null) {
      return `${tons(minTarget)} - ${tons(maxTarget)} ตัน`;
    } else if (minTarget !== null  && minTarget !== 0) {
      return `ตั้งแต่ ${tons(minTarget)} ตัน`;
    } else if (minTarget === 0 && maxTarget !== null) {
      return `ไม่เกิน ${tons(maxTarget)} ตัน`;
    } else {
      return "-";
    }
  };

  return (
    <CardContainer>
      <PageTitle
        title="ระดับร้านค้า"
        extraSpan={8}
        extra={
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {
              canExport && selectedTab === "history" && (
                <Button
                  typeButton="border-primary"
                  style={{ width: "auto" }}
                  icon={<DownloadOutlined />}
                  title="Export ข้อมูลระดับร้านค้า"
                  loading={isExporting}
                  onClick={() => setIsExportModalOpen(true)}
                />
              )
            }
            {
              canExport && selectedTab === "audit" && (
                <Button
                  typeButton="border-primary"
                  style={{ width: "auto" }}
                  icon={<DownloadOutlined />}
                  title="Export Audit Log"
                  loading={isExporting}
                  onClick={handleExportAuditLog}
                />
              )
            }
            {
              canCreate && selectedTab === "config" && (
                <Button
                  typeButton="primary"
                  style={{ width: "auto" }}
                  icon={<PlusOutlined />}
                  title="สร้างปีสิทธิประโยชน์"
                  onClick={() => navigate("/ShopTier/create/benefit")}
                />
              )
            }
          </div>
        }
      />
      <div style={{ marginTop: 4, marginBottom: 16 }}>
        <Text fontSize={13} color="Text3">
          อัปเดตล่าสุดเมื่อ {overviewUpdatedAt}
        </Text>
      </div>
      {
        selectedTab === "history" && (
          <KpiRow aria-label={`จำนวนร้านต่อระดับ ${config.companyLabel}`} aria-busy={isOverviewLoading}>
            {/* Placeholders only for the very first load; later loads keep the previous chips. */}
            {isOverviewLoading && config.tiers.map((t) => (
              <KpiSkeleton key={t.key}>
                <KpiSkeletonLine $width="45%" $height={26} />
                <KpiSkeletonLine $width="70%" $height={16} $gap={10} />
                <KpiSkeletonLine $width="55%" $height={12} $gap={8} />
              </KpiSkeleton>
            ))}
            {!isOverviewLoading && tierLevels.map((tier) => (
              <KpiChip
                key={tier.tierLevelId}
                type="button"
                $bg={tier.color}
                $active={tierFilter === tier.tierLevelId}
                onClick={() => toggleTierFilter(tier.tierLevelId)}
              >
                <KpiValue $color={kpiTextColor(tier.color)}>
                  {tier.customerCount || 0}
                  <KpiUnit>ร้าน</KpiUnit>
                </KpiValue>
                <KpiTitle $color={kpiTextColor(tier.color)}>{tier.tierName}</KpiTitle>
                <KpiSub $color={kpiTextColor(tier.color)}>{rangeTier(tier.minTarget, tier.maxTarget)}</KpiSub>
              </KpiChip>
            ))}
          </KpiRow>
        )
      }

      {
        selectedTab === "history" && (
          <NavWarningBanner>
            <WarningOutlined style={{ color: color.warning, fontSize: 20 }} />
            <Text fontSize={14} fontFamily="Sarabun" color="Text1">
              มี {pendingNavCount} ร้าน ที่เปลี่ยนระดับแล้วยังไม่ได้คีย์เข้า Navision ให้คีย์ในระบบ Navision ก่อน
              แล้วจึงกลับมาทำเครื่องหมายในรายการ{" "}
              <NavWarningLink onClick={() => setIsNavPending(!isNavPending)} style={{ textDecoration: "underline", cursor: "pointer", fontFamily: "Sarabun" }}>{isNavPending ? "ปิดตัวกรอง" : "กรองดูร้านค้า"}</NavWarningLink>
            </Text>
          </NavWarningBanner>
        )
      }


      <ToolbarRow style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        {
          (selectedTab === "history" || selectedTab === "audit") && <SearchInput
            style={{ width: 320 }}
            placeholder="ค้นหารหัสร้านค้า / ชื่อร้านค้า"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        }
        {
          selectedTab === "history" && (
            <div style={{display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: 180, marginRight: 8 }}>
                <Select fontSize={14} data={zones} value={zone!} onChange={(v) => setZone(v)} placeholder="เขตทั้งหมด" />
              </div>
              <div style={{ width: 180 }}>
                <Select fontSize={14} data={statusOptions} value={status} onChange={(v) => setStatus(v)} />
              </div>
            </div>
          )
        }
        {
          selectedTab === "audit" && (
            <div style={{display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: 180, marginRight: 8 }}>
                <Select fontSize={14} data={zones} value={zone!} onChange={(v) => setZone(v)} placeholder="เขตทั้งหมด" />
              </div>
              <div style={{ width: 120, marginRight: 8 }}>
                <Select fontSize={14} data={yearOptions} value={year!} onChange={(v) => setYear(v)} placeholder="ปีทั้งหมด" />
              </div>
              <div style={{ width: 250 }}>
                <Select fontSize={14} data={actionOptions} value={action!} onChange={(v) => setAction(v)} placeholder="การดำเนินการ" />
              </div>
            </div>
          )
        }
      </ToolbarRow>
      <AntdTabs data={tabsData} defaultTab={selectedTab} onChange={(v) => setSelectedTab(v)} />
      <ExportShopTierModal
        open={isExportModalOpen}
        availableYears={availableYears?.responseData?.historyPrograms || []}
        loading={isExporting}
        onCancel={() => setIsExportModalOpen(false)}
        onExport={(exportYear) => {
          setIsExportModalOpen(false);
          handleExportShopTier(exportYear);
        }}
      />
    </CardContainer>
  );
}

export default ShopTierPage;
